import { ITeam, IUserProfile, Team, UserProfile } from "../../../base/common/auth.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { UserInfoResponse } from "needle-cloud";
import type { LogtoUserCustomData } from "needle-cloud";
import { ILogService } from "../../log/common/log.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { INotificationService } from "../../notifications/common/notifications.js";

export const ContextKeyLoggedIn = 'isLoggedIn';

export const IIdentitiyService = createDecorator<IIdentitiyService>("identityService");

export interface LoginOptions {
    /** Land on the registration screen instead of sign-in (first_screen=register). */
    signUp?: boolean;
}

export interface IIdentitiyService {

    get me(): IUserProfile | null;
    get isLoggedIn(): boolean;
    get isLoginProcessRunning(): boolean;

    login(options?: LoginOptions): Promise<IUserProfile | null>;
    logout(): Promise<void>;

    teams: readonly ITeam[];

    /** The team resolved from the user's stored selection, or the default_org custom_data, or null. */
    get currentTeam(): ITeam | null;

    /**
     * Ensure the logged-in user has a team. If none is resolved yet, ask the
     * backend to create-or-confirm a default team, then refresh the profile so
     * `currentTeam` reflects it. Returns the resolved team, or null if not logged
     * in or the ensure failed.
     *
     * Users who signed up outside the website (chrome extension / MCP) can lack a
     * default team until this runs — team-dependent features (AI chat) should call
     * this instead of failing when `currentTeam` is null.
     */
    ensureTeam(): Promise<ITeam | null>;

    /**
     * Override the current team selection. Pass null to revert to the Logto default.
     * The selection is persisted in application storage so it applies across all websites.
     */
    setCurrentTeam(team: ITeam | null): void;

    /**
     * Get a short-lived access token scoped to the given org.
     * Returns null if the token cannot be obtained (e.g. user not logged in).
     */
    getAccessToken(orgId: string): Promise<{ accessToken: string, expiresAt: string } | null>;

    readonly onDidChange: Event<IIdentitiyService | null>;

}


export abstract class AbstractLogtoIdentitiyService extends Disposable implements IIdentitiyService {
    protected _onDidChange = new Emitter<IIdentitiyService>();
    readonly onDidChange = this._onDidChange.event;

    private static readonly STORAGE_KEY_SELECTED_TEAM = "identity.selectedTeamId";

    private readonly isLoggedInKey = this.contextKeyService.createKey<boolean>(ContextKeyLoggedIn, false);

    constructor(
        @ILogService protected readonly logger: ILogService,
        @IAnalyticsService protected readonly analytics: IAnalyticsService | null,
        @IContextKeyService protected readonly contextKeyService: IContextKeyService,
        @IStorageService protected readonly storageService: IStorageService,
        @INotificationService protected readonly notificationService: INotificationService
    ) {
        super();
    }


    get me(): IUserProfile {
        return this._me!;
    }

    get teams(): readonly ITeam[] {
        return this._teams;
    }

    private _currentTeam: ITeam | null = null;

    get currentTeam(): ITeam | null {
        // Prefer user-selected team from application storage
        const storedId = this.storageService.get(AbstractLogtoIdentitiyService.STORAGE_KEY_SELECTED_TEAM, StorageScope.APPLICATION);
        if (storedId) {
            const stored = this._teams.find(t => t.id === storedId);
            if (stored) return stored;
        }
        return this._currentTeam;
    }

    setCurrentTeam(team: ITeam | null): void {
        if (team) {
            this.storageService.store(
                AbstractLogtoIdentitiyService.STORAGE_KEY_SELECTED_TEAM,
                team.id,
                StorageScope.APPLICATION,
                StorageTarget.USER,
            );
        } else {
            this.storageService.remove(AbstractLogtoIdentitiyService.STORAGE_KEY_SELECTED_TEAM, StorageScope.APPLICATION);
        }
        this._onDidChange.fire(this);
    }

    abstract getAccessToken(orgId: string): Promise<{ accessToken: string, expiresAt: string } | null>;

    private _ensureTeamPromise: Promise<ITeam | null> | null = null;

    async ensureTeam(): Promise<ITeam | null> {
        if (!this.isLoggedIn) return null;
        if (this.currentTeam) return this.currentTeam;
        // Dedupe concurrent callers (AI chat sync + a lazy send can race).
        this._ensureTeamPromise ??= this._runEnsureTeam().finally(() => {
            this._ensureTeamPromise = null;
        });
        return this._ensureTeamPromise;
    }

    private async _runEnsureTeam(): Promise<ITeam | null> {
        // Create-or-confirm the default team on the backend (idempotent). Doing this
        // before the refresh removes the signup→webhook race — we only re-read the
        // profile once the team is guaranteed to exist server-side.
        try {
            await this.ensureDefaultTeamOnBackend();
        } catch (e) {
            this.logger.error("Auth: ensureDefaultTeam failed", e);
        }
        // Re-fetch the profile so organization_data + default_org (and thus
        // currentTeam) reflect the ensured team.
        try {
            const info = await this.getUserInfo();
            if (info) this.updateUserProfile(info);
        } catch (e) {
            this.logger.error("Auth: refresh after ensureTeam failed", e);
        }
        return this.currentTeam;
    }

    /**
     * Create-or-confirm the logged-in user's default team on the backend
     * (idempotent). Called by {@link ensureTeam} when no team is resolved.
     */
    protected abstract ensureDefaultTeamOnBackend(): Promise<void>;

    get isLoggedIn(): boolean {
        return this._me != null;
    }

    get isLoginProcessRunning(): boolean {
        return this._loginPromise !== null;
    }

    private _loginPromise: Promise<IUserProfile | null> | null = null;
    private _lastLoginError: unknown = null;
    async login(options?: LoginOptions): Promise<IUserProfile | null> {
        this.logger.info("Auth: Starting login process");
        this.analytics?.logEvent("auth:login-attempt", { signUp: options?.signUp === true });
        return this.internalLogin(options).then(async res => {
            if (res) {
                this.notificationService?.success(`Logged in as ${res.name ?? res.id}`);
                await this.analytics?.logEvent("auth:login-success", { userId: res.id });
                return this._me;
            }
            else {
                const error = this._lastLoginError;
                const reason = error instanceof Error
                    ? `${error.name}: ${error.message}`
                    : error ? String(error) : "no-user-info";

                // @TODO: timeout should not halt app
                // Login might redirect async but it's not properly awaited it seems in all cases
                const t = setTimeout(async () => {
                    this.notificationService?.error("Login failed. Please check your network connection and try again.", {});
                    // internalLogin converts errors to null, so surface the captured
                    // reason here — without it login failures can't be diagnosed
                    await this.analytics?.logEvent("auth:login-failed", { reason: reason.substring(0, 300) });
                }, 2000);

                return null;
            }
        });
    }

    async logout(): Promise<void> {
        this.logger.info("Auth: Starting logout process");
        this.analytics?.logEvent("auth:logout", { userId: this._me?.id || "unknown" });
        return this.runLogoutFlow().then(() => {
            this.logger.debug("Auth: Logout process completed");
            this._me = null;
            this._teams.length = 0;
            this._onDidChange.fire(this);
        });
    }


    /** Login without analytics */
    protected async internalLogin(options?: LoginOptions): Promise<IUserProfile | null> {
        if (this._loginPromise) {
            this.logger.debug("Auth: Login already in progress, returning existing promise");
            return this._loginPromise;
        }
        this._lastLoginError = null;
        this._loginPromise = this.runLoginFlow(options).then((user) => {
            if (user) {
                this.updateUserProfile(user);
                return this._me;
            }
            else {
                this.logger.debug("Auth: Login flow completed but no user info returned");
            }
            return this._me;
        })
            .catch(err => {
                this.logger.error("Auth: Login process failed", err);
                this._lastLoginError = err;
                return null;
            })
            .finally(() => {
                this._loginPromise = null;
            });
        return this._loginPromise;
    }

    protected _me: UserProfile | null = null;
    protected readonly _teams: ITeam[] = [];

    protected abstract runLoginFlow(options?: LoginOptions): Promise<UserInfoResponse | null>;
    protected abstract getUserInfo(): Promise<UserInfoResponse | null>;
    protected abstract runLogoutFlow(): Promise<void>;

    protected updateUserProfile(user: UserInfoResponse) {

        this.isLoggedInKey.set(!!user);

        if (user) {
            this.logger.debug("Updating user profile:", user);

            this._me ??= new UserProfile(user.sub, user.name || user.username, user.email || "", user.picture);
            this._me.id = user.sub;
            this._me.name = user.name;
            this._me.email = user.email;
            this._me.avatarUrl = user.picture;

            if (user.organization_data) {
                for (const org of user.organization_data) {
                    const team = this._teams.find(t => t.id === org.id) || new Team(org.id, org.name);
                    team.name = org.name;
                    if (!this._teams.includes(team)) {
                        this._teams.push(team);
                    }
                }
            }

            // Resolve currentTeam from custom_data.default_org
            const defaultOrgId = (user.custom_data as LogtoUserCustomData | undefined)?.default_org;
            if (typeof defaultOrgId === "string") {
                this._currentTeam = this._teams.find(t => t.id === defaultOrgId) ?? null;
            } else {
                this._currentTeam = null;
            }
            this.logger.debug("User profile updated:", this.isLoggedIn, this._me, this._teams);
            this._onDidChange.fire(this);
        }
        else if (this._me) {
            this.logger.debug("Clearing user profile");
            this._me = null;
            this._teams.length = 0;
            this._onDidChange.fire(this);
        }
        else {
            this.logger.debug("No user profile to update");
        }
    }
}
